import { useState, useEffect, useCallback, useRef } from "react";
import {
  Package,
  Play,
  Square,
  CheckCircle,
  AlertCircle,
  X,
  Clock,
  Database,
  Timer,
  Terminal,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { productsApi } from "../services/productsApi";

const ProductsManagement = () => {
  const { translate } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [ingestionStatus, setIngestionStatus] = useState(null);
  const [vectorStats, setVectorStats] = useState(null);
  const [ingestionHistory, setIngestionHistory] = useState([]);
  const [ingestionLogs, setIngestionLogs] = useState([]);
  const prevIngestionRef = useRef(null);
  const logsEndRef = useRef(null);

  // Auto-scroll logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [ingestionLogs]);

  const addLog = useCallback((message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setIngestionLogs((prev) => [...prev.slice(-49), { timestamp, message, type }]);
  }, []);

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productsApi.getSchedulerStatus();
      setSchedulerStatus(response);
      setError(null);
    } catch (err) {
      setError(
        translate("failedToFetchSchedulerStatus") ||
          "Failed to fetch scheduler status"
      );
    } finally {
      setLoading(false);
    }
  }, [translate]);

  const fetchIngestionStatus = useCallback(async () => {
    try {
      const response = await productsApi.getIngestionStatus();
      const prev = prevIngestionRef.current;

      // Track state transitions for logs and history
      if (prev && prev.status !== response.status) {
        if (response.status === "running") {
          addLog(translate("ingestionStarted") || "Ingestion started...", "info");
        } else if (response.status === "completed") {
          addLog(response.message || translate("ingestionCompleted") || "Ingestion completed successfully", "success");
          // Add to history
          setIngestionHistory((h) => [
            {
              id: Date.now(),
              status: "completed",
              started_at: prev.started_at || response.started_at,
              completed_at: response.completed_at,
              message: response.message,
            },
            ...h.slice(0, 9),
          ]);
        } else if (response.status === "failed") {
          addLog(response.error || translate("ingestionFailed") || "Ingestion failed", "error");
          setIngestionHistory((h) => [
            {
              id: Date.now(),
              status: "failed",
              started_at: prev.started_at || response.started_at,
              completed_at: response.completed_at,
              error: response.error,
            },
            ...h.slice(0, 9),
          ]);
        }
      }

      // Log messages while running
      if (response.status === "running" && response.message && response.message !== prev?.message) {
        addLog(response.message, "info");
      }

      prevIngestionRef.current = response;
      setIngestionStatus(response);
    } catch (err) {
      console.warn("Failed to fetch ingestion status:", err);
    }
  }, [addLog, translate]);

  const fetchVectorStats = useCallback(async () => {
    try {
      const response = await productsApi.getVectorStoreStats();
      setVectorStats(response);
    } catch (err) {
      console.warn("Failed to fetch vector stats:", err);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    let cancelled = false;
    const loadInitial = async () => {
      try {
        await Promise.all([fetchSchedulerStatus(), fetchIngestionStatus(), fetchVectorStats()]);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };
    loadInitial();
    return () => { cancelled = true; };
  }, [fetchSchedulerStatus, fetchIngestionStatus, fetchVectorStats]);

  // Poll ingestion status when running
  useEffect(() => {
    let interval;
    if (ingestionStatus && ingestionStatus.status === "running") {
      interval = setInterval(() => {
        fetchIngestionStatus();
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [ingestionStatus, fetchIngestionStatus]);

  // Refresh vector stats when ingestion completes
  useEffect(() => {
    if (ingestionStatus && ingestionStatus.status === "completed") {
      fetchVectorStats();
    }
    // Intentionally keyed on the status transition only, not the full
    // ingestionStatus object (new identity every poll would re-run this).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionStatus?.status, fetchVectorStats]);

  const handleRunIngestion = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setIngestionLogs([]);

      addLog(translate("triggeringIngestion") || "Triggering product ingestion...", "info");

      const response = await productsApi.runIngestion();
      setSuccess(
        response.message ||
          translate("ingestionStartedSuccessfully") ||
          "Product ingestion started successfully"
      );

      addLog(translate("ingestionTriggered") || "Ingestion triggered, waiting for response...", "info");

      await fetchSchedulerStatus();
      await fetchIngestionStatus();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToStartIngestion") ||
        "Failed to start product ingestion";

      setError(errorMessage);
      addLog(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStartScheduler = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const started = await productsApi.startScheduler();
      const schedule = started?.daily_times
        ? `${translate("atTimes") || "At"} ${started.daily_times.split(",").join(", ")}`
        : started?.interval_minutes
          ? `${translate("every") || "Every"} ${started.interval_minutes} ${translate("minutes") || "min"}`
          : null;
      setSuccess(
        [
          translate("schedulerStartedSuccessfully") ||
            "Product ingestion scheduler started successfully",
          schedule,
        ]
          .filter(Boolean)
          .join(" — ")
      );
      await fetchSchedulerStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          translate("failedToStartScheduler") ||
          "Failed to start scheduler"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStopScheduler = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await productsApi.stopScheduler();
      setSuccess(
        translate("schedulerStoppedSuccessfully") ||
          "Product ingestion scheduler stopped successfully"
      );
      await fetchSchedulerStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          translate("failedToStopScheduler") ||
          "Failed to stop scheduler"
      );
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Derived data
  const productsCollection = vectorStats?.collections?.find((c) => c.name === "products");
  const totalProducts = productsCollection?.vectors_count || 0;

  const lastIngestionTime = ingestionStatus?.completed_at || ingestionStatus?.started_at || null;

  const formatDuration = (start, end) => {
    if (!start || !end) return "—";
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    if (isNaN(diffMs) || diffMs < 0) return "—";
    const secs = Math.floor(diffMs / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const remainSecs = secs % 60;
    return `${mins}m ${remainSecs}s`;
  };

  const statusBgColor = (status) => {
    if (status === "completed") return "rgba(22,163,74,0.08)";
    if (status === "running") return "rgba(37,99,235,0.08)";
    if (status === "failed") return "rgba(239,68,68,0.08)";
    return "var(--color-surface-raised)";
  };

  const statusBorderColor = (status) => {
    if (status === "completed") return "var(--color-success)";
    if (status === "running") return "var(--color-info)";
    if (status === "failed") return "var(--color-error)";
    return "var(--color-border)";
  };

  // KPI Card component
  const KpiCard = ({ icon: Icon, label, value, subtitle, status }) => (
    <div className="card rounded-xl border border-[var(--color-border)] p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>
            {label}
          </p>
          <p className="text-2xl font-semibold mt-1" style={{ color: "var(--color-text-primary)" }}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
              {subtitle}
            </p>
          )}
        </div>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: status === "success" ? "rgba(62,207,142,0.1)"
              : status === "error" ? "var(--color-error-muted)"
              : status === "warning" ? "rgba(245,158,11,0.1)"
              : "var(--color-mint-muted)",
            color: status === "success" ? "var(--color-mint)"
              : status === "error" ? "var(--color-error)"
              : status === "warning" ? "var(--color-warning)"
              : "var(--color-mint)",
          }}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--color-skeleton-light)]" />
          <div>
            <div className="h-7 w-56 rounded-lg mb-2 bg-[var(--color-skeleton)]" />
            <div className="h-4 w-80 rounded bg-[var(--color-skeleton-light)]" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card rounded-xl border border-[var(--color-border)] p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-24 rounded bg-[var(--color-skeleton-light)]" />
                  <div className="h-7 w-16 rounded bg-[var(--color-skeleton)]" />
                  <div className="h-3 w-20 rounded bg-[var(--color-skeleton-light)]" />
                </div>
                <div className="w-10 h-10 rounded-xl bg-[var(--color-skeleton-light)]" />
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-48 rounded-xl bg-[var(--color-skeleton-light)]" />
          <div className="h-48 rounded-xl bg-[var(--color-skeleton-light)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Package className="h-8 w-8" style={{ color: "var(--color-mint)" }} />
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {translate("productsManagement") || "Products Management"}
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("productsManagementDescription") ||
                "Manage product ingestion and scheduler"}
            </p>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div
          className="p-4 rounded-xl flex items-center justify-between"
          style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)", color: "var(--color-error)" }}
        >
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-3" />
            <span className="text-sm">{error}</span>
          </div>
          <button onClick={clearMessages} className="hover:opacity-70">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div
          className="p-4 rounded-xl flex items-center justify-between"
          style={{ backgroundColor: "var(--color-mint-muted)", border: "1px solid var(--color-mint-border)" }}
        >
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 mr-3" style={{ color: "var(--color-mint)" }} />
            <span className="text-sm" style={{ color: "var(--color-mint-dark)" }}>{success}</span>
          </div>
          <button onClick={clearMessages} style={{ color: "var(--color-text-muted)" }} className="hover:opacity-70">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Feature 1: KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Database}
          label={translate("totalProducts") || "Total Products"}
          value={vectorStats ? totalProducts.toLocaleString() : "—"}
          subtitle={translate("inVectorStore") || "In vector store"}
          status="success"
        />
        <KpiCard
          icon={Timer}
          label={translate("schedulerLabel") || "Scheduler"}
          value={
            schedulerStatus
              ? schedulerStatus.job_status?.status === "active"
                ? translate("active") || "Active"
                : translate("stopped") || "Stopped"
              : "—"
          }
          subtitle={
            schedulerStatus?.job_status?.interval_minutes
              ? `${translate("every") || "Every"} ${schedulerStatus.job_status.interval_minutes} ${translate("minutes") || "min"}`
              : schedulerStatus?.job_status?.daily_times
                ? `${translate("atTimes") || "At"} ${schedulerStatus.job_status.daily_times.split(",").join(", ")}`
                : null
          }
          status={schedulerStatus?.job_status?.status === "active" ? "success" : "warning"}
        />
        <KpiCard
          icon={Package}
          label={translate("ingestionStatusLabel") || "Ingestion Status"}
          value={
            !ingestionStatus || ingestionStatus.status === "idle"
              ? translate("idle") || "Idle"
              : ingestionStatus.status === "completed"
              ? translate("completed") || "Completed"
              : ingestionStatus.status === "running"
              ? translate("running") || "Running"
              : translate("failed") || "Failed"
          }
          subtitle={ingestionStatus?.message || null}
          status={
            ingestionStatus?.status === "completed" ? "success"
            : ingestionStatus?.status === "failed" ? "error"
            : ingestionStatus?.status === "running" ? "success"
            : undefined
          }
        />
        <KpiCard
          icon={Clock}
          label={translate("lastIngestion") || "Last Ingestion"}
          value={
            lastIngestionTime
              ? new Date(lastIngestionTime).toLocaleDateString()
              : "—"
          }
          subtitle={
            lastIngestionTime
              ? new Date(lastIngestionTime).toLocaleTimeString()
              : translate("noDataYet") || "No data yet"
          }
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Ingestion Job Status */}
        <div className="card rounded-xl border border-[var(--color-border)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {translate("productIngestionJob") || "Product Ingestion Job"}
            </h3>
            {/* Scheduler Control Button */}
            {schedulerStatus ? (
              schedulerStatus.job_status?.status === "active" ? (
                <button
                  onClick={handleStopScheduler}
                  disabled={loading}
                  className="btn-destructive disabled:opacity-50"
                >
                  {loading ? (
                    <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
                  ) : (
                    <Square className="h-4 w-4 mr-2" />
                  )}
                  {translate("stopScheduler") || "Stop Scheduler"}
                </button>
              ) : (
                <button
                  onClick={handleStartScheduler}
                  disabled={loading}
                  className="btn-primary disabled:opacity-50"
                >
                  {loading ? (
                    <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {translate("startScheduler") || "Start Scheduler"}
                </button>
              )
            ) : null}
          </div>

          {schedulerStatus ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                <span className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("status") || "Status"}
                </span>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    schedulerStatus.job_status?.status === "active"
                      ? "badge-active"
                      : "badge-inactive"
                  }`}
                >
                  {schedulerStatus.job_status?.status === "active"
                    ? translate("running") || "Running"
                    : translate("stopped") || "Stopped"}
                </span>
              </div>

              {schedulerStatus.job_status?.interval_minutes && (
                <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("intervalMinutes") || "Interval"}
                  </span>
                  <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {schedulerStatus.job_status.interval_minutes}{" "}
                    {translate("minutes") || "minutes"}
                  </span>
                </div>
              )}

              {/* The !interval_minutes half is redundant by construction, and kept deliberately: a job holds exactly ONE trigger, so the backend never reports both keys, and re-deriving the exclusivity here keeps that invariant visible at the call site instead of silently resting on it (RPAA-265). */}
              {!schedulerStatus.job_status?.interval_minutes &&
                schedulerStatus.job_status?.daily_times && (
                <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("dailyTimes") || "Daily times"}
                  </span>
                  <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {schedulerStatus.job_status.daily_times.split(",").join(", ")}
                  </span>
                </div>
              )}

              {schedulerStatus.job_status?.next_run_time && (
                <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("nextRunTime") || "Next Run"}
                  </span>
                  <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {new Date(
                      schedulerStatus.job_status.next_run_time
                    ).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div style={{ width: '32px', height: '32px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }} />
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                  {translate("loading") || "Loading..."}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Manual Ingestion + Run Button */}
        <div className="card rounded-xl border border-[var(--color-border)] p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {translate("manualIngestionStatus") || "Manual Ingestion Status"}
              </h3>
              <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("manualIngestionDescription") ||
                  "Shows status of manually triggered ingestions only"}
              </p>
            </div>
            <button
              onClick={handleRunIngestion}
              disabled={
                loading ||
                (ingestionStatus && ingestionStatus.status === "running")
              }
              className="btn-primary disabled:opacity-50 flex-shrink-0"
            >
              {loading ? (
                <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {translate("runIngestion") || "Run Ingestion"}
            </button>
          </div>

          <div>
            {ingestionStatus && ingestionStatus.status !== "idle" ? (
              <div className="space-y-4">
                <div
                  className="p-4 rounded-xl"
                  style={{
                    backgroundColor: statusBgColor(ingestionStatus.status),
                    borderLeft: `4px solid ${statusBorderColor(ingestionStatus.status)}`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {ingestionStatus.status === "completed" && (
                        <CheckCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-success)" }} />
                      )}
                      {ingestionStatus.status === "running" && (
                        <div style={{ width: '20px', height: '20px', border: '2px solid var(--color-info)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
                      )}
                      {ingestionStatus.status === "failed" && (
                        <AlertCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-error)" }} />
                      )}
                      <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                        {translate("status") || "Status"}
                      </span>
                    </div>
                    <span
                      className={`px-3 py-1 text-sm font-medium rounded-full ${
                        ingestionStatus.status === "completed"
                          ? "badge-active"
                          : ingestionStatus.status === "running"
                          ? "badge-mint"
                          : "badge-inactive"
                      }`}
                    >
                      {ingestionStatus.status === "completed"
                        ? translate("completed") || "Completed"
                        : ingestionStatus.status === "running"
                        ? translate("running") || "Running"
                        : ingestionStatus.status === "failed"
                        ? translate("failed") || "Failed"
                        : ingestionStatus.status}
                    </span>
                  </div>

                  {ingestionStatus.message && (
                    <p className="mt-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      {ingestionStatus.message}
                    </p>
                  )}
                </div>

                {(ingestionStatus.started_at || ingestionStatus.completed_at) && (
                  <div className="grid grid-cols-1 gap-3">
                    {ingestionStatus.started_at && (
                      <div className="flex items-center justify-between p-2 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                        <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
                          {translate("startedAt") || "Started At"}
                        </span>
                        <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                          {ingestionStatus.started_at}
                        </span>
                      </div>
                    )}
                    {ingestionStatus.completed_at && (
                      <div className="flex items-center justify-between p-2 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                        <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
                          {translate("completedAt") || "Completed At"}
                        </span>
                        <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                          {ingestionStatus.completed_at}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {ingestionStatus.error && (
                  <div
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)" }}
                  >
                    <div className="flex items-start">
                      <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" style={{ color: "var(--color-error)" }} />
                      <span className="text-sm" style={{ color: "var(--color-error)" }}>
                        {ingestionStatus.error}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <Play className="h-8 w-8" style={{ color: "var(--color-text-muted)" }} />
                </div>
                <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("noManualIngestion") || "No manual ingestion running"}
                </p>
                <p className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                  {translate("clickRunToStartManual") ||
                    "Click 'Run Ingestion' to manually process products"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feature 3: Ingestion Logs (real-time terminal) */}
      {ingestionLogs.length > 0 && (
        <div className="card rounded-xl border border-[var(--color-border)] overflow-hidden">
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ backgroundColor: "var(--color-sidebar-bg)" }}
          >
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4" style={{ color: "var(--color-mint)" }} />
              <span className="text-sm font-medium text-white">
                {translate("ingestionLogs") || "Ingestion Logs"}
              </span>
              {ingestionStatus?.status === "running" && (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: "var(--color-mint)", animation: "pulse 1.5s infinite" }}
                />
              )}
            </div>
            <button
              onClick={() => setIngestionLogs([])}
              className="text-xs px-2 py-1 rounded transition-colors"
              style={{ color: "var(--color-text-sidebar)" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--color-sidebar-hover)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              {translate("clear") || "Clear"}
            </button>
          </div>
          <div
            className="overflow-y-auto font-mono text-xs leading-relaxed p-4"
            style={{
              backgroundColor: "var(--color-sidebar-bg)",
              maxHeight: "200px",
              color: "var(--color-text-sidebar)",
            }}
          >
            {ingestionLogs.map((log, i) => (
              <div key={i} className="flex gap-3 py-0.5">
                <span style={{ color: "var(--color-text-muted)", flexShrink: 0 }}>
                  [{log.timestamp}]
                </span>
                <span
                  style={{
                    color:
                      log.type === "error" ? "var(--color-error)"
                      : log.type === "success" ? "var(--color-success)"
                      : "var(--color-text-sidebar)",
                  }}
                >
                  {log.type === "error" ? "✗ " : log.type === "success" ? "✓ " : "→ "}
                  {log.message}
                </span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}

      {/* Feature 2: Ingestion History */}
      {ingestionHistory.length > 0 && (
        <div className="card rounded-xl border border-[var(--color-border)]">
          <div className="px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
            <h3 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {translate("ingestionHistory") || "Ingestion History"}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>
              {translate("ingestionHistoryDescription") || "Recent ingestion runs from this session"}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>
                    {translate("status") || "Status"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>
                    {translate("startedAt") || "Started At"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>
                    {translate("duration") || "Duration"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>
                    {translate("details") || "Details"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ingestionHistory.map((entry) => (
                  <tr
                    key={entry.id}
                    style={{ borderTop: "1px solid var(--color-border)" }}
                  >
                    <td className="px-6 py-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          entry.status === "completed" ? "badge-active" : "badge-inactive"
                        }`}
                      >
                        {entry.status === "completed"
                          ? translate("completed") || "Completed"
                          : translate("failed") || "Failed"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      {entry.started_at
                        ? new Date(entry.started_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-6 py-3 text-sm font-mono" style={{ color: "var(--color-text-secondary)" }}>
                      {formatDuration(entry.started_at, entry.completed_at)}
                    </td>
                    <td className="px-6 py-3 text-sm" style={{ color: entry.error ? "var(--color-error)" : "var(--color-text-secondary)" }}>
                      {entry.error || entry.message || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManagement;
